import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocalizationProvider } from './context/LocalizationContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { useUserData } from './hooks/useUserData';
import Dashboard from './components/Dashboard';
import WelcomeScreen from './components/WelcomeScreen';
import VitalsCheck from './components/VitalsCheck';
import Consultation from './components/Consultation';
import EndSession from './components/EndSession';
import LoadingScreen from './components/LoadingScreen';
import Assessment from './components/Assessment';
import AssessmentDetail from './components/Assessment/AssessmentDetail';
import ChatManager from './components/ChatManager';
import { AuthCallback } from './components/AuthCallback';
import './App.css';
import TopBar from './components/shared/TopBar';
import WebSocketStatus from './components/WebSocketStatus';
import backgroundVideo from './assets/videos/background.mp4';

const AppContent = () => {
  const { userData, loading, error } = useUserData();
  const { isLoading: authLoading, isAuthenticated, error: authError, login } = useAuth();
  const { setUserData } = useAppContext();
  const lastStoredUserData = useRef(null);
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Redirect to login if not authenticated (except on callback route)
  useEffect(() => {
    // Don't redirect if we're on the callback route
    if (location.pathname === '/auth/callback') {
      hasRedirected.current = false; // Reset on callback route
      return;
    }

    // Don't redirect if we're processing the callback (tokens are being set)
    const processingCallback = sessionStorage.getItem('processing_callback');
    if (processingCallback) {
      return;
    }

    // Wait for auth to finish loading
    // Also add a small delay after callback to allow session to update
    if (!authLoading && !isAuthenticated) {
      // If we've already redirected in this session, skip to prevent loops
      if (hasRedirected.current) {
        return;
      }

      // Don't store /end as redirect target - go to home instead
      const redirectPath = location.pathname !== '/' && location.pathname !== '/end'
        ? location.pathname
        : '/';

      if (redirectPath !== '/') {
        sessionStorage.setItem('redirect_after_login', redirectPath);
      }

      hasRedirected.current = true;
      // Redirect to login immediately
      login('keycloak-new');
    }
  }, [authLoading, isAuthenticated, location.pathname, login]);

  useEffect(() => {
    if (userData) {
      if (JSON.stringify(userData) !== JSON.stringify(lastStoredUserData.current)) {
        setUserData(userData);
        lastStoredUserData.current = userData;
      }
    }
  }, [userData, setUserData]);

  // Show loading screen while user data or authentication is loading
  if (loading || authLoading) {
    return <LoadingScreen />;
  }

  // Show authentication error if auth fails
  if (authError) {
    return (
      <div className="app">
        <div className="error-container">
          <h2>Authentication Error</h2>
          <p>{authError}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Don't render app content if not authenticated (will redirect)
  if (!isAuthenticated && location.pathname !== '/auth/callback') {
    return <LoadingScreen />;
  }

  // Show error if user data fails to load
  if (error) {
    console.error('App error:', error);
  }

  return (
    <div className="app">
      <video
        className="app-video-background"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={backgroundVideo} type="video/mp4" />
      </video>
      <TopBar />
      <WebSocketStatus />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<WelcomeScreen userData={userData} />} />
        <Route path="/dashboard" element={<Dashboard userData={userData} />} />
        <Route path="/welcome" element={<WelcomeScreen userData={userData} />} />
        <Route path="/vitals" element={<VitalsCheck userData={userData} />} />
        <Route path="/consultation/:podId?" element={<Consultation userData={userData} />} />
        <Route path="/assessment" element={<Assessment userData={userData} />} />
        <Route path="/assessment/:type" element={<AssessmentDetail />} />
        <Route path="/chat-manager" element={<ChatManager userData={userData} />} />
        <Route path="/end" element={<EndSession userData={userData} />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <LocalizationProvider>
      <AuthProvider>
        <AppProvider>
          <WebSocketProvider>
            <Router>
              <AppContent />
            </Router>
          </WebSocketProvider>
        </AppProvider>
      </AuthProvider>
    </LocalizationProvider>
  );
};

export default App;
